import React, { useState } from 'react';
import { FaTimes, FaDownload, FaUser, FaEnvelope, FaPhone, FaBuilding, FaSpinner } from 'react-icons/fa';
import API_URL from '../../config/api';
import './DownloadRequestModal.css';

const DownloadRequestModal = ({ isOpen, onClose, catalog, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Name, Email, and Phone are required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await fetch(`${API_URL.CATALOGS}/download-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          catalogId: catalog._id,
          catalogTitle: catalog.title,
          catalogCategory: catalog.category
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Show appropriate message based on email status
        if (data.emailSent && data.confirmationSent) {
          setSuccess('Download request submitted successfully! A confirmation email has been sent to your email address. Our sales team will contact you soon.');
        } else if (data.emailSent) {
          setSuccess('Download request submitted successfully! Our sales team will contact you soon. (Note: Confirmation email may not have been sent)');
        } else {
          setSuccess('Download request received successfully! We have logged your request and our sales team will contact you soon. (Note: Email confirmation was not sent due to server configuration)');
        }
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        // Close modal after 4 seconds (longer to read the message)
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 4000);
      } else {
        setError(data.message || 'Failed to send download request');
      }
    } catch (error) {
      console.error('Error sending download request:', error);
      setError(`Error sending request: ${error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      setError('');
      setSuccess('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="download-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <FaDownload className="download-icon" />
            <h3>Request Catalog Download</h3>
          </div>
          <button className="close-btn" onClick={handleClose} disabled={loading}>
            <FaTimes />
          </button>
        </div>

        <div className="catalog-info">
          <h4>{catalog?.title}</h4>
          <p className="catalog-category">{catalog?.category}</p>
        </div>

        <form onSubmit={handleSubmit} className="download-form">
          <div className="form-row">
            <div className="form-group">
              <label>
                <FaUser className="input-icon" />
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter your full name"
              />
            </div>

            <div className="form-group">
              <label>
                <FaEnvelope className="input-icon" />
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email address"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <FaPhone className="input-icon" />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="Enter your phone number"
              />
            </div>

            <div className="form-group">
              <label>
                <FaBuilding className="input-icon" />
                Company Name
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Enter your company name"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Additional Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Any specific requirements or questions about this catalog..."
              rows="4"
            />
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-actions">
            <button type="button" onClick={handleClose} className="cancel-btn" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Sending Request...
                </>
              ) : (
                <>
                  <FaDownload />
                  Send Download Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DownloadRequestModal;
